import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import Copyable from '../../../components/utils/Copyable';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import {
  GetUploadedFilesBIZ
} from '../../../redux/actions/business/uploadedFilesActions';

import global from "../../../global.scss";

// file_size is stored as a raw byte count, which is unreadable past a few KB.
const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (bytes === null || bytes === undefined || Number.isNaN(size)) return null;
  if (size === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(
    Math.floor(Math.log(Math.abs(size)) / Math.log(1024)),
    units.length - 1
  );
  const value = size / Math.pow(1024, exponent);

  // bytes are always whole; everything above gets one decimal
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`;
};

const UploadedFilesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.business);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  
  const [uploadedFilesList, setUploadedFilesList] = useState([]);
  const [uploadedFilesTotal, setUploadedFilesTotal] = useState(0);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "File name",
      sort: "file_name",
    },
    {
      label: "File path",
      sort: "file_path",
    },
    {
      label: "File size",
      sort: "file_size",
    },
    {
      label: "File type",
      sort: "file_type",
    },
    {
      label: "Uploaded by",
      sort: null,
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    }
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetUploadedFilesBIZ(page+1, rowsPerPage, query, setLoading));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      console.log(state);
      formatUploadedFilesList(state.uploadedFilesList.records || []);
      setUploadedFilesTotal(state.uploadedFilesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatUploadedFilesList = (list) => {
    setUploadedFilesList(list);
  };


  return (
    <Box>

      <TopBar
        title={"Uploaded files"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        />
      
      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          uploadedFilesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No uploaded file found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                uploadedFilesList.map((row, index) => (
                  <TableRow 
                    key={index}
                    className="table__clickable-row">
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.file_name || <NA />}</TableCell>
                    <TableCell>
                      {
                        row.file_path
                        ?
                        <Tooltip title={row.file_path}>
                          <Link
                            href={row.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="always"
                            sx={{
                              color: global['secondary_light'],
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              maxWidth: "320px",
                            }}>
                            <Box
                              component="span"
                              className="longText">
                              {row.file_path}
                            </Box>
                            <OpenInNewIcon sx={{ fontSize: "14px", flexShrink: 0 }} />
                          </Link>
                        </Tooltip>
                        :
                        <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${row.file_size ?? 0} bytes`}>
                        <Typography sx={{ whiteSpace: "nowrap" }}>
                          {formatFileSize(row.file_size) || <NA />}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.file_type || <NA />}</TableCell>
                    <TableCell>{row.uploaded_by || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={uploadedFilesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default UploadedFilesPage;